import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ShoppingCart,
  Plus,
  Minus,
  CreditCard,
  ArrowLeft,
  Check,
  Clock,
  Users,
  Utensils,
  X,
} from "lucide-react";
import type { MenuItem, OrderItem } from "../types";
import { supabase } from "../lib/supabase";
import { RESTAURANT_SETTINGS_ID } from "../constants";

/**
 * IMPORTANT (matches the schema you pasted):
 * - Categories table is: public.menu_categories (id, store_id, name, sort_order)
 * - Menu items table is: public.menu_items (id, store_id, name, price, category_id)
 * - There is NO orders / order_items table in your schema excerpt.
 * - So kiosk checkout will write to:
 *    - public.sales (store_id, type, payment, total)
 *    - public.sales_items (stores_id, sale_id, item_id, item_name, quantity, price)
 *
 * If you do have an orders system elsewhere, tell me and I’ll swap the persistence layer.
 */

type Step = "welcome" | "menu" | "cart" | "checkout" | "confirmation";

type RestaurantSettings = {
  has_kiosk: boolean;
};

type CategoryRow = {
  id: string;
  store_id: string;
  name: string;
  sort_order: number;
  created_at: string;
};

type MenuItemRow = {
  id: string;
  store_id: string;
  name: string;
  price: number;
  category_id: string | null;
  created_at: string;
  updated_at: string;
};

interface MealCustomizationState {
  menuItem: MenuItem;
  isMeal: boolean;
  selectedSide?: string;
  selectedDrink?: string;
}

function safeInt(s: string): number | null {
  const n = parseInt(String(s || "").trim(), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Cart key so "Burger meal (chips+coke)" doesn't merge with "Burger meal (water+salad)" or "Just burger".
 */
function cartKey(item: Partial<OrderItem>): string {
  return [
    item.menu_item_id ?? "",
    item.is_meal ? "meal" : "single",
    item.meal_side ?? "",
    item.meal_drink ?? "",
  ].join("|");
}

export default function Kiosk() {
  const [currentStep, setCurrentStep] = useState<Step>("welcome");
  const [settings, setSettings] = useState<RestaurantSettings | null>(null);

  const [storeId, setStoreId] = useState<string | null>(null);

  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const [cart, setCart] = useState<OrderItem[]>([]);
  const [customerName, setCustomerName] = useState<string>("");
  const [orderType, setOrderType] = useState<"dine_in" | "takeaway">("dine_in");
  const [tableNumber, setTableNumber] = useState<string>("");

  const [showMealCustomization, setShowMealCustomization] = useState(false);
  const [mealCustomization, setMealCustomization] = useState<MealCustomizationState | null>(null);

  const resetTimeoutRef = useRef<number | null>(null);

  // --------- INIT ---------
  useEffect(() => {
    // cleanup timeout on unmount
    return () => {
      if (resetTimeoutRef.current) window.clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    fetchSettings();

    // determine store_id from store_users (matches your schema)
    resolveStoreId().then((sid) => {
      setStoreId(sid);
    });

    // Listen for storage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "restaurant_settings" && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          setSettings(newSettings);
        } catch (error) {
          console.error("Error parsing settings from storage:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Real-time settings subscription
    const settingsSubscription = supabase
      .channel("restaurant_settings")
      .on("postgres_changes", { event: "*", schema: "public", table: "restaurant_settings" }, () => {
        fetchSettings();
      })
      .subscribe();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      settingsSubscription.unsubscribe();
    };
  }, []);

  // once storeId is known, load catalog
  useEffect(() => {
    if (!storeId) return;
    fetchCategories(storeId);
    fetchMenuItems(storeId);
  }, [storeId]);

  // whenever categories load, ensure we have a selected category
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  // --------- DATA ---------
  const resolveStoreId = async (): Promise<string | null> => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) return null;

      // store_users: (store_id, user_id)
      const { data, error } = await supabase
        .from("store_users")
        .select("store_id")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error resolving store_id:", error);
        return null;
      }
      return data?.store_id ?? null;
    } catch (e) {
      console.error("Error resolving store_id:", e);
      return null;
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("restaurant_settings")
        .select("has_kiosk")
        .eq("id", RESTAURANT_SETTINGS_ID)
        .maybeSingle();

      if (error) {
        console.error("Error fetching settings:", error);
        setSettings({ has_kiosk: true });
      } else {
        setSettings(data || { has_kiosk: true });
      }
    } catch (error) {
      console.error("Error:", error);
      setSettings({ has_kiosk: true });
    }
  };

  const fetchCategories = async (sid: string) => {
    const { data, error } = await supabase
      .from("menu_categories")
      .select("*")
      .eq("store_id", sid)
      .order("sort_order", { ascending: true });

    if (error) {
      console.error("Error fetching menu_categories:", error);
      setCategories([]);
      return;
    }

    const cats = (data || []) as CategoryRow[];
    setCategories(cats);
  };

  const fetchMenuItems = async (sid: string) => {
    const { data, error } = await supabase
      .from("menu_items")
      .select("*")
      .eq("store_id", sid)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error fetching menu_items:", error);
      setMenuItems([]);
      return;
    }

    const rows = (data || []) as MenuItemRow[];

    // Transform rows to your MenuItem type.
    // Your older kiosk used fields like description/image_url/available/etc — your schema doesn't have those.
    // We'll provide safe defaults for UI.
    const transformed: MenuItem[] = rows.map((r) => ({
      id: r.id,
      name: r.name,
      price: Number(r.price || 0),
      // in your earlier code, `category` is a string id
      category: r.category_id || "",
      description: "",
      image_url: null as any,
      available: true,
      preparation_time: 0,
      created_at: r.created_at,
      // meal fields (not in schema) — leave disabled by default
      can_be_meal: false,
      meal_price_add: 0,
      meal_sides: [],
      meal_drinks: [],
    }));

    setMenuItems(transformed);
  };

  // --------- CART ---------
  const totalQty = useMemo(() => cart.reduce((sum, i) => sum + (i.quantity || 0), 0), [cart]);

  const getTotalAmount = () => {
    return cart.reduce((total, item) => {
      let itemPrice = item.menu_item?.price || 0;
      if (item.is_meal && item.menu_item?.meal_price_add) itemPrice += item.menu_item.meal_price_add;
      return total + itemPrice * item.quantity;
    }, 0);
  };

  const addOrIncrementCartItem = (newItem: OrderItem) => {
    const key = cartKey(newItem);
    const existing = cart.find((ci) => cartKey(ci) === key);
    if (existing) {
      setCart((prev) => prev.map((ci) => (ci.id === existing.id ? { ...ci, quantity: ci.quantity + 1 } : ci)));
    } else {
      setCart((prev) => [...prev, newItem]);
    }
  };

  const addToCart = (menuItem: MenuItem) => {
    // Only open meal modal if meal is enabled for this item
    if (menuItem.can_be_meal) {
      setMealCustomization({ menuItem, isMeal: false });
      setShowMealCustomization(true);
      return;
    }

    const newItem: OrderItem = {
      id: Date.now().toString(),
      menu_item_id: menuItem.id,
      quantity: 1,
      menu_item: menuItem,
      is_meal: false,
      meal_side: undefined,
      meal_drink: undefined,
    };

    addOrIncrementCartItem(newItem);
  };

  const updateQuantity = (itemId: string, change: number) => {
    setCart((prev) =>
      prev
        .map((item) => {
          if (item.id !== itemId) return item;
          const newQuantity = item.quantity + change;
          if (newQuantity <= 0) return null;
          return { ...item, quantity: newQuantity };
        })
        .filter(Boolean) as OrderItem[]
    );
  };

  // For menu-page +/- buttons: increment the first matching NON-MEAL line for that item
  const incrementSimpleLine = (menuItemId: string, delta: number) => {
    const line = cart.find((ci) => ci.menu_item_id === menuItemId && !ci.is_meal);
    if (!line) return;
    updateQuantity(line.id, delta);
  };

  const simpleLineQty = (menuItemId: string) => {
    const line = cart.find((ci) => ci.menu_item_id === menuItemId && !ci.is_meal);
    return line?.quantity || 0;
  };

  // --------- CHECKOUT / PERSISTENCE (sales + sales_items) ---------
  const submitOrder = async () => {
    if (!storeId) {
      console.error("No storeId available; cannot create sale");
      setCurrentStep("confirmation");
      resetAfterConfirmation();
      return;
    }

    const total = getTotalAmount();

    try {
      // Create sale
      const { data: saleRow, error: saleError } = await supabase
        .from("sales")
        .insert([
          {
            store_id: storeId,
            type: orderType === "dine_in" ? "dine_in" : "takeaway",
            payment: "card",
            total: total,
          },
        ])
        .select()
        .single();

      if (saleError) {
        console.error("Error creating sale:", saleError);
        setCurrentStep("confirmation");
        resetAfterConfirmation();
        return;
      }

      const saleId = saleRow?.id;
      if (!saleId) {
        console.error("Sale created but no id returned");
        setCurrentStep("confirmation");
        resetAfterConfirmation();
        return;
      }

      // Create sale items
      // NOTE: your schema has sales_items.quantity numeric (not int), price numeric, item_id uuid.
      const saleItemsPayload = cart.map((item) => {
        const basePrice = Number(item.menu_item?.price || 0);
        const mealAdd = item.is_meal ? Number(item.menu_item?.meal_price_add || 0) : 0;
        const unitPrice = basePrice + mealAdd;

        return {
          stores_id: storeId,
          sale_id: saleId,
          item_id: item.menu_item_id,
          item_name: item.menu_item?.name || "Item",
          quantity: item.quantity,
          price: unitPrice, // unit price; total can be computed later
        };
      });

      const { error: itemsError } = await supabase.from("sales_items").insert(saleItemsPayload);
      if (itemsError) {
        console.error("Error creating sale items:", itemsError);
        // You might want to rollback sale here (delete sale + items) depending on your needs.
      }

      // Optional: you can store customerName/tableNumber somewhere else if you add columns.
      // Your schema doesn't include them on sales, so we just keep it UI-only.

      setCurrentStep("confirmation");
      resetAfterConfirmation();
    } catch (e) {
      console.error("submitOrder error:", e);
      setCurrentStep("confirmation");
      resetAfterConfirmation();
    }
  };

  const resetAfterConfirmation = () => {
    if (resetTimeoutRef.current) window.clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = window.setTimeout(() => {
      setCurrentStep("welcome");
      setCart([]);
      setCustomerName("");
      setTableNumber("");
      setOrderType("dine_in");
      // keep selectedCategory as-is
    }, 8000);
  };

  const filteredMenuItems = useMemo(() => {
    if (!selectedCategory) return [];
    return menuItems.filter((item) => item.category === selectedCategory);
  }, [menuItems, selectedCategory]);

  // --------- SETTINGS GATE ---------
  if (settings && !settings.has_kiosk) {
    return (
      <div className="h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center max-w-md">
          <div className="bg-gray-100 p-6 rounded-2xl mb-6">
            <Users size={64} className="text-gray-400 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Kiosk Disabled</h2>
          <p className="text-gray-600 mb-6">Customer kiosks have been disabled in restaurant settings.</p>
          <div className="text-sm text-gray-500">Please contact staff for assistance with your order.</div>
        </div>
      </div>
    );
  }

  // --------- WELCOME ---------
  if (currentStep === "welcome") {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-600 to-purple-700 flex flex-col">
        <div className="bg-white shadow-lg p-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Welcome to Forkcast</h1>
            <p className="text-xl text-gray-600">Touch to start your order</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-white">
            <div className="mb-8">
              <Utensils size={120} className="mx-auto mb-6 opacity-90" />
              <h2 className="text-6xl font-bold mb-4">Ready to Order?</h2>
              <p className="text-2xl opacity-90">Fresh food made to order</p>
            </div>

            <button
              onClick={() => setCurrentStep("menu")}
              className="bg-white text-blue-600 px-16 py-8 rounded-2xl text-3xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-2xl transform hover:scale-105"
            >
              Start Order
            </button>
          </div>
        </div>

        <div className="bg-white p-4 text-center text-gray-600">
          <p className="text-lg">Touch anywhere to begin • Available for dine-in and takeaway</p>
        </div>
      </div>
    );
  }

  // --------- CONFIRMATION ---------
  if (currentStep === "confirmation") {
    return (
      <div className="h-screen bg-gradient-to-br from-green-500 to-emerald-600 flex flex-col items-center justify-center text-white">
        <div className="text-center">
          <div className="bg-white bg-opacity-20 rounded-full p-8 w-32 h-32 mx-auto mb-8 flex items-center justify-center">
            <Check size={64} />
          </div>
          <h2 className="text-6xl font-bold mb-6">Order Confirmed!</h2>
          <p className="text-2xl mb-8 opacity-90">Thank you for your order! We’re preparing it now.</p>

          <div className="bg-white bg-opacity-20 p-6 rounded-2xl">
            <div className="text-2xl font-bold mb-2">Order Total</div>
            <div className="text-5xl font-bold">£{getTotalAmount().toFixed(2)}</div>
          </div>

          <div className="mt-8 flex items-center justify-center opacity-75">
            <Clock size={24} className="mr-3" />
            <span className="text-xl">Estimated time: 15–20 minutes</span>
          </div>
        </div>
      </div>
    );
  }

  // --------- MAIN LAYOUT (menu/cart/checkout) ---------
  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          {currentStep !== "menu" && (
            <button
              onClick={() => {
                if (currentStep === "cart") setCurrentStep("menu");
                else if (currentStep === "checkout") setCurrentStep("cart");
              }}
              className="flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 px-6 py-4 rounded-xl font-bold text-lg transition-colors text-white"
            >
              <ArrowLeft size={24} className="mr-2" />
              Back
            </button>
          )}

          <div className="flex-1 text-center">
            <h1 className="text-3xl font-bold text-white">Forkcast</h1>
            <p className="text-blue-100 text-sm">Self-Service Ordering</p>
          </div>

          {cart.length > 0 && currentStep === "menu" && (
            <button
              onClick={() => setCurrentStep("cart")}
              className="bg-orange-500 text-white px-6 py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition-colors flex items-center shadow-lg"
            >
              <ShoppingCart size={24} className="mr-2" />
              <div>
                <div>My Order</div>
                <div className="text-sm">{totalQty} items</div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {currentStep === "menu" && (
          <div className="h-full flex flex-col">
            {/* Categories */}
            <div className="bg-white shadow-lg p-6 flex-shrink-0">
              {categories.length === 0 ? (
                <div className="text-center text-gray-600 font-semibold">
                  No categories found for this store.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-6 py-4 rounded-xl font-bold text-xl transition-colors ${
                        selectedCategory === category.id
                          ? "bg-blue-600 text-white shadow-lg"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="flex-1 p-4 overflow-y-auto">
              {selectedCategory && filteredMenuItems.length === 0 ? (
                <div className="text-center py-16 text-gray-600 font-semibold">
                  No items in this category.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6">
                  {filteredMenuItems.map((item) => {
                    const qty = simpleLineQty(item.id);
                    return (
                      <div
                        key={item.id}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:scale-105 min-h-[280px]"
                      >
                        {/* Image (fallback since schema doesn't include image_url) */}
                        <div className="h-40 bg-gray-200 overflow-hidden">
                          <img
                            src={
                              (item as any).image_url ||
                              "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400"
                            }
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src =
                                "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400";
                            }}
                          />
                        </div>

                        <div className="p-4">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3rem]">
                            {item.name}
                          </h3>

                          <div className="flex justify-between items-center mb-3">
                            <span className="text-xl font-bold text-blue-600">£{item.price.toFixed(2)}</span>

                            {item.can_be_meal && (
                              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-bold">
                                +Meal £{Number(item.meal_price_add || 0).toFixed(2)}
                              </span>
                            )}
                          </div>

                          {qty > 0 && !item.can_be_meal ? (
                            <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg">
                              <button
                                onClick={() => incrementSimpleLine(item.id, -1)}
                                className="bg-red-500 hover:bg-red-600 text-white w-10 h-10 rounded-full transition-colors flex items-center justify-center"
                              >
                                <Minus size={20} />
                              </button>
                              <span className="text-xl font-bold text-gray-800 mx-4">{qty}</span>
                              <button
                                onClick={() => incrementSimpleLine(item.id, 1)}
                                className="bg-green-500 hover:bg-green-600 text-white w-10 h-10 rounded-full transition-colors flex items-center justify-center"
                              >
                                <Plus size={20} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => addToCart(item)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition-colors flex items-center justify-center text-lg"
                            >
                              <Plus size={20} className="mr-2" />
                              Add to Order
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === "cart" && (
          <div className="h-full p-6 overflow-y-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Your Order</h2>

            {cart.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart size={64} className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-xl font-bold">Your cart is empty</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-8">
                  {cart.map((item) => (
                    <div key={item.id} className="bg-white rounded-xl p-4 flex items-center justify-between shadow-lg">
                      <div className="flex items-center flex-1">
                        <img
                          src={
                            (item.menu_item as any)?.image_url ||
                            "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400"
                          }
                          alt={item.menu_item?.name}
                          className="w-16 h-16 object-cover rounded-lg mr-4"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src =
                              "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400";
                          }}
                        />
                        <div className="flex-1">
                          <h4 className="font-bold text-lg text-gray-900 mb-1">{item.menu_item?.name}</h4>
                          {item.is_meal && (
                            <div className="text-sm text-blue-600 mb-1">
                              Side: {item.meal_side || "-"} • Drink: {item.meal_drink || "-"}
                            </div>
                          )}
                          <p className="text-blue-600 font-bold text-lg">
                            £
                            {(() => {
                              let itemPrice = item.menu_item?.price || 0;
                              if (item.is_meal && item.menu_item?.meal_price_add) itemPrice += item.menu_item.meal_price_add;
                              return (itemPrice * item.quantity).toFixed(2);
                            })()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-full transition-colors flex items-center justify-center"
                        >
                          <Minus size={20} />
                        </button>
                        <span className="text-xl font-bold w-10 text-center text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-full transition-colors flex items-center justify-center"
                        >
                          <Plus size={20} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl p-6 shadow-xl">
                  <div className="flex justify-between items-center text-3xl font-bold mb-6">
                    <span className="text-gray-900">Total:</span>
                    <span className="text-blue-600">£{getTotalAmount().toFixed(2)}</span>
                  </div>

                  <button
                    onClick={() => setCurrentStep("checkout")}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-5 rounded-xl text-xl font-bold transition-colors"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {currentStep === "checkout" && (
          <div className="h-full p-6 overflow-y-auto">
            <div className="max-w-lg mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Order Details</h2>

              <div className="bg-white rounded-xl p-6 shadow-xl space-y-6">
                <div>
                  <label className="block text-xl font-bold text-gray-900 mb-4">How would you like your order?</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setOrderType("dine_in")}
                      className={`p-6 rounded-xl font-bold text-lg transition-colors ${
                        orderType === "dine_in"
                          ? "bg-blue-600 text-white shadow-lg"
                          : "bg-gray-100 text-gray-700 hover:bg-blue-50"
                      }`}
                    >
                      <Utensils size={32} className="mx-auto mb-3" />
                      Dine In
                    </button>
                    <button
                      onClick={() => setOrderType("takeaway")}
                      className={`p-6 rounded-xl font-bold text-lg transition-colors ${
                        orderType === "takeaway"
                          ? "bg-blue-600 text-white shadow-lg"
                          : "bg-gray-100 text-gray-700 hover:bg-blue-50"
                      }`}
                    >
                      <ShoppingCart size={32} className="mx-auto mb-3" />
                      Takeaway
                    </button>
                  </div>
                </div>

                {orderType === "dine_in" && (
                  <div>
                    <label className="block text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <Users size={24} className="mr-3 text-blue-600" />
                      Table Number
                    </label>
                    <input
                      type="number"
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      className="w-full p-4 border-2 border-gray-300 rounded-lg text-2xl text-center font-bold focus:border-blue-500 focus:outline-none"
                      placeholder="Enter table number"
                      min="1"
                      max="99"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">Name (Optional)</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Enter your name"
                  />
                </div>

                <div className="border-t-2 border-gray-200 pt-6">
                  <div className="flex justify-between text-3xl font-bold mb-6">
                    <span className="text-gray-900">Total:</span>
                    <span className="text-blue-600">£{getTotalAmount().toFixed(2)}</span>
                  </div>

                  <button
                    onClick={submitOrder}
                    disabled={cart.length === 0 || (orderType === "dine_in" && !safeInt(tableNumber))}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-5 rounded-xl text-xl font-bold transition-colors flex items-center justify-center"
                  >
                    <CreditCard size={24} className="mr-3" />
                    Place Order & Pay
                  </button>

                  <p className="text-center text-gray-600 mt-4 text-base">Payment will be processed securely</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Meal Customization Modal (only used if can_be_meal items exist in your MenuItem type/data) */}
      {showMealCustomization && mealCustomization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">{mealCustomization.menuItem.name}</h3>
                <button
                  onClick={() => {
                    setShowMealCustomization(false);
                    setMealCustomization(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <X size={28} />
                </button>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={() => setMealCustomization({ ...mealCustomization, isMeal: false })}
                  className={`flex-1 p-4 rounded-lg font-bold text-lg transition-colors ${
                    !mealCustomization.isMeal ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Just Item
                  <div className="text-base font-normal">£{mealCustomization.menuItem.price.toFixed(2)}</div>
                </button>

                <button
                  onClick={() => setMealCustomization({ ...mealCustomization, isMeal: true })}
                  className={`flex-1 p-4 rounded-lg font-bold text-lg transition-colors ${
                    mealCustomization.isMeal ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Make it a Meal
                  <div className="text-base font-normal">
                    £
                    {(
                      mealCustomization.menuItem.price + Number(mealCustomization.menuItem.meal_price_add || 0)
                    ).toFixed(2)}
                  </div>
                </button>
              </div>
            </div>

            <div className="p-6">
              {mealCustomization.isMeal && (
                <div className="space-y-6 mb-6">
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-3">Choose your side:</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {(mealCustomization.menuItem.meal_sides || ["Chips", "Onion Rings", "Sweet Potato Fries", "Side Salad"]).map(
                        (side) => (
                          <button
                            key={side}
                            onClick={() => setMealCustomization({ ...mealCustomization, selectedSide: side })}
                            className={`p-3 rounded-lg text-base font-semibold transition-colors ${
                              mealCustomization.selectedSide === side
                                ? "bg-green-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {side}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-800 mb-3">Choose your drink:</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {(mealCustomization.menuItem.meal_drinks || ["Coke", "Pepsi", "Orange Juice", "Water", "Coffee"]).map(
                        (drink) => (
                          <button
                            key={drink}
                            onClick={() => setMealCustomization({ ...mealCustomization, selectedDrink: drink })}
                            className={`p-3 rounded-lg text-base font-semibold transition-colors ${
                              mealCustomization.selectedDrink === drink
                                ? "bg-green-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {drink}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowMealCustomization(false);
                    setMealCustomization(null);
                  }}
                  className="flex-1 bg-gray-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    if (!mealCustomization.isMeal || (mealCustomization.selectedSide && mealCustomization.selectedDrink)) {
                      const newItem: OrderItem = {
                        id: Date.now().toString(),
                        menu_item_id: mealCustomization.menuItem.id,
                        quantity: 1,
                        menu_item: mealCustomization.menuItem,
                        is_meal: mealCustomization.isMeal,
                        meal_side: mealCustomization.selectedSide,
                        meal_drink: mealCustomization.selectedDrink,
                      };
                      addOrIncrementCartItem(newItem);
                      setShowMealCustomization(false);
                      setMealCustomization(null);
                    }
                  }}
                  disabled={mealCustomization.isMeal && (!mealCustomization.selectedSide || !mealCustomization.selectedDrink)}
                  className="flex-1 bg-green-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Add to Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
